import React, { FC, useState, useEffect, useContext, useRef } from 'react';
import { AuthZeroApolloContext } from '@src/views/components/util/external/AuthZeroApolloClient';
import { ChatMessage, useCentralCalls } from '@src/rest/CentralCalls';
import './Dash.scss';
import { Icon } from 'semantic-ui-react';

interface AiFloatingResponseProps {
  input: string;
  context: string;
  title: string;
  onClose: () => void;
}

const AiFloatingResponse: FC<AiFloatingResponseProps> = ({ context, input, title, onClose }) => {
  const [ chatHistory, setChatHistory ] = useState<ChatMessage[]>( [] ),
        [ userInput, setUserInput ]     = useState( '' ),
        [ loadingDots, setLoadingDots ] = useState('.'),
        [ isTyping, setIsTyping ]       = useState( false ),
        chatEndRef                      = useRef<HTMLDivElement>( null );
  const { GetAIResponse } = useCentralCalls();

  useEffect(() => {
    const fetchInitialAiResponse = async () => {
      console.log("initial response...");
      console.log(input);
      console.log(context);
      if (title && title !== '') {
        setChatHistory((prev) => [
          ...prev,
          { Role: 'divider', Content: title } // Add a special divider entry
        ]);
      }
      if (input === '' || !input) {
        addMessageWithTypingEffect({ Role: 'assistant', Content: 'Hello! How can I assist you today?' });
      }
      else {
        const initialResponse = await handleGetAIResponse(input, context);
        if (initialResponse) {
          addMessageWithTypingEffect({ Role: 'assistant', Content: initialResponse });
        }
      }
    };
    fetchInitialAiResponse();
  }, [input, context, title]);

  useEffect(() => {
    if (isTyping) {
      const typingEffect = () => {
        setLoadingDots((prev) => (prev.length < 3 ? prev + '.' : '.'));
      };
      const interval = setInterval(typingEffect, 500);
      return () => clearInterval(interval);
    }
  }, [isTyping]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleGetAIResponse = async (prompt: string, systemContext: string): Promise<string | null> => {
    try {
      setIsTyping(true);
      console.log('chistory, prompt, context...');
      console.log(JSON.stringify(chatHistory));
      console.log(prompt);
      console.log(systemContext);
      const response = await GetAIResponse(prompt, systemContext, chatHistory);
      setIsTyping(false);
      return response.response || null;
    } catch (error) {
      setIsTyping(false);
      console.error('Error fetching AI response:', error);
      return 'An error occurred while fetching the response.';
    }
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const newUserMessage = { Role: 'user', Content: userInput };
    setChatHistory((prev) => [...prev, newUserMessage]);
    const aiResponse = await handleGetAIResponse(userInput, 'Keep Answers releated to biopharma commerci');
    if (aiResponse) {
      addMessageWithTypingEffect({ Role: 'assistant', Content: aiResponse });
    }

    setUserInput('');
  };

  const addMessageWithTypingEffect = (message: ChatMessage) => {
    const { Role, Content } = message;
    let currentIndex = 0;

    const typingEffect = () => {
      if (currentIndex < Content.length) {
        setChatHistory((prev) => {
          const updatedHistory = [...prev];
          if (updatedHistory[updatedHistory.length - 1]?.Role === Role) {
            updatedHistory[updatedHistory.length - 1].Content += Content[currentIndex];
          } else {
            updatedHistory.push({ Role, Content: Content[currentIndex] });
          }
          return updatedHistory;
        });
        currentIndex++;
        setTimeout(typingEffect, 15);
      }
    };

    typingEffect();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="ai-floating-response">
      <div className="ai-response-container">
        <div className="title-bar">
          <Icon name='bolt' />
          <h2>Cor-IQ</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="chat-history">
          {chatHistory.map((chat, index) => (
            chat.Role === 'divider' ? (
              <div key={index} className="chat-divider">
                <hr className="divider-line" />
                <span className="divider-text">{chat.Content}</span>
                <hr className="divider-line" />
              </div>
            ) : (
              <div
                key={index}
                className={`chat-bubble ${chat.Role === 'user' ? 'user-bubble' : 'ai-bubble'
                  }`}
              >
                {chat.Content}
              </div>
            )
          ))}
          {isTyping && <div className="loading-bubble">{loadingDots}</div>}
          <div ref={chatEndRef} /> {/* This ensures we scroll to the bottom */}
        </div>
        <div className="input-container">
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
            className="input-box"
          />
          <button className="send-button" onClick={handleSendMessage}>
            &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiFloatingResponse;